import {
  Divider,
  Flex,
  HStack,
  Input,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";

import { FormControl } from "../../../components";
import { formatDate } from "../../../utils/datetime";
import {
  AiInterviewerFragment,
  AiInterviewerVideoMode,
  PositionListItemFragment,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useIsScreenOrgAtsConnected } from "../../hooks/useIsScreenOrgAtsConnected";
import { useIsScreenPositionRequired } from "../../hooks/useIsScreenPositionRequired";
import PositionSelect from "../PositionSelect/PositionSelect";
import { UserAccessRow } from "../ShareModalV2/UserAccessRow";
import { ShareAiInterviewerButton } from "./ShareAiInterviewerButton";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import { useSpeechSpeedUpdate } from "./useSpeechSpeedUpdate";

interface AiInterviewerSettingsInterviewSettingsProps {
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
  isShareEnabled?: boolean;
}

const AiInterviewerSettingsInterviewSettings: React.FC<
  AiInterviewerSettingsInterviewSettingsProps
> = ({ readOnly, aiInterviewer, isShareEnabled }) => {
  const { register, setValue, watch } = useAiInterviewerFormContext();
  const isVideoExperienceEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:video_experience"
  );

  const ttsSettings = useMemo(
    () =>
      JSON.parse(
        aiInterviewer.aiInterviewerAgentSettings?.ttsSettings ?? "{}"
      ) as { voice_settings?: { speed?: number } },
    [aiInterviewer.aiInterviewerAgentSettings?.ttsSettings]
  );
  const [currentSpeed, setCurrentSpeed] = useState(
    ttsSettings.voice_settings?.speed ?? 1.0
  );

  const isPositionRequired = useIsScreenPositionRequired();
  const isAtsConnected = useIsScreenOrgAtsConnected();
  const selectedPositionId = watch("positionId");

  const handlePositionChange = useCallback(
    (position: PositionListItemFragment | undefined) => {
      if (readOnly) return;
      setValue("positionId", position?.id, { shouldDirty: true });
    },
    [readOnly, setValue]
  );

  const { updateSpeechSpeed } = useSpeechSpeedUpdate({
    aiInterviewerId: aiInterviewer.id,
    ttsSettingsJsonString:
      aiInterviewer.aiInterviewerAgentSettings?.ttsSettings,
  });

  const handleSpeedChange = (speed: number): void => {
    setCurrentSpeed(speed);
  };

  const handleSpeedCommit = useCallback(
    (speed: number): void => {
      if (readOnly) return;
      updateSpeechSpeed(speed);
    },
    [readOnly, updateSpeechSpeed]
  );

  return (
    <>
      <VStack spacing={4} align="stretch" w="100%">
        {isPositionRequired && (
          <FormControl id="position" displayName="Position">
            <PositionSelect
              positionId={selectedPositionId}
              onSelect={handlePositionChange}
              disabled={readOnly}
              isClearable={!isPositionRequired}
              size="md"
              customStyles={{
                control: (provided: Record<string, unknown>) => ({
                  ...provided,
                  fontSize: "14px",
                  borderRadius: "6px",
                  borderColor: "#E2E8F0",
                  "&:hover": {
                    borderColor: "#CBD5E0",
                  },
                }),
              }}
            />
          </FormControl>
        )}
        <FormControl id="name" displayName="AI interviewer name">
          <Input
            {...register("name")}
            placeholder="Enter AI interviewer name"
            fontSize="sm"
            borderRadius="md"
            isDisabled={readOnly}
          />
        </FormControl>
        <FormControl
          id="displayName"
          displayName="Job title (will be shown to candidates)"
        >
          <Input
            {...register("displayName")}
            placeholder="Enter job title"
            fontSize="sm"
            borderRadius="md"
            isDisabled={readOnly}
          />
        </FormControl>
        {isVideoExperienceEnabled && (
          <InlineFormControl id="videoSetting" displayName="Video setting">
            <Select
              {...register("videoSetting")}
              fontSize="sm"
              borderRadius="md"
              width="auto"
              minW="120px"
              isDisabled={readOnly}
            >
              <option value={AiInterviewerVideoMode.Disabled}>Disabled</option>
              <option value={AiInterviewerVideoMode.Optional}>Optional</option>
              <option value={AiInterviewerVideoMode.Required}>Required</option>
            </Select>
          </InlineFormControl>
        )}
        {isAtsConnected && (
          <InlineFormControl
            id="aiInterviewerExpiration"
            displayName="Invite expires after"
          >
            <Select
              {...register("inviteExpirationDays", { valueAsNumber: true })}
              fontSize="sm"
              borderRadius="md"
              width="auto"
              minW="120px"
              isDisabled={readOnly}
            >
              {[1, 3, 7, 14, 30, 60].map((days) => (
                <option key={days} value={days}>
                  {days} {days === 1 ? "day" : "days"}
                </option>
              ))}
            </Select>
          </InlineFormControl>
        )}
        <FormControl id="speechSpeed" displayName="Speech Speed">
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" color="gray.600">
                Slower
              </Text>
              <Text fontSize="sm" color="gray.600">
                Faster
              </Text>
            </HStack>
            <Slider
              value={currentSpeed}
              onChange={handleSpeedChange}
              onChangeEnd={handleSpeedCommit}
              min={0.7}
              max={1.2}
              step={0.1}
              isDisabled={readOnly}
              colorScheme="blue"
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Current: {currentSpeed.toFixed(1)}x{" "}
            </Text>
          </VStack>
        </FormControl>
      </VStack>

      <Divider mt="6" />
      <VStack spacing={6} align="stretch" mt="6" w="100%">
        <DisplayField
          label="Author"
          value={aiInterviewer.initialVersion?.createdBy?.fullName ?? ""}
        />
        <DisplayField
          label="Date created"
          value={formatDate(aiInterviewer.initialVersion?.createdAt, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        />
        <DisplayField
          label="Last updated by"
          value={aiInterviewer.currentVersion?.createdBy?.fullName ?? ""}
        />
        <DisplayField
          label="Last updated"
          value={formatDate(aiInterviewer.currentVersion?.createdAt, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        />
      </VStack>

      {isShareEnabled && (
        <>
          <Divider mt="6" />
          <VStack spacing={3} align="stretch" mt="6" w="100%">
            <Flex align="baseline" justify="space-between" w="100%" mb="1">
              <Text
                sx={{
                  color: "gray.600",
                  fontSize: "sm",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "16px",
                }}
              >
                SHARED WITH
              </Text>
              <ShareAiInterviewerButton aiInterviewer={aiInterviewer} ghost />
            </Flex>

            <VStack spacing="2" align="stretch" w="100%">
              {aiInterviewer.memberships &&
              aiInterviewer.memberships.length > 0 ? (
                aiInterviewer.memberships?.map((membership) => {
                  const firstName = membership.user.firstName || "";
                  const lastName = membership.user.lastName || "";
                  const fullName = `${firstName} ${lastName}`.trim();
                  const roleLabel =
                    membership.role === "EDITOR" ? "Editor" : "Viewer";
                  return (
                    <UserAccessRow
                      key={membership.id}
                      layout="readonly"
                      user={{
                        id: membership.user.id,
                        firstName: firstName || null,
                        lastName: lastName || null,
                        email: membership.user.email,
                        fullName: fullName || membership.user.email,
                      }}
                      primaryText={fullName || membership.user.email}
                      secondaryText={membership.user.email}
                      roleLabel={roleLabel}
                    />
                  );
                })
              ) : (
                <Text fontSize="sm" color="gray.600">
                  Not shared with anyone yet.
                </Text>
              )}
            </VStack>
          </VStack>
        </>
      )}
    </>
  );
};

const InlineFormControl: typeof FormControl = (props) => (
  <FormControl
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap="2"
    displayNameProps={{ mb: 0, minW: "140px" }}
    {...props}
  />
);

const DisplayField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Flex align="center" justifyContent="space-between" gap="2" fontSize="sm">
    <Text color="gray.600">{label}</Text>
    <Text>{value}</Text>
  </Flex>
);

export default AiInterviewerSettingsInterviewSettings;
